/**
 * bundle-discount-override.js
 *
 * ⚠️  TEMPLATE RESTRICTION:
 *    Loaded only by the bundle-subscription buy box snippets
 *    (buy-box-bundle-subscription.liquid / -v2.liquid). Bails out
 *    silently when .bundle-subscription-buybox is not in the DOM.
 *
 * WHY THIS EXISTS
 *   Skio applies its own tiered volume discount (10% for 1 pack,
 *   15% for 2 packs, 20% for 3+ packs) by pushing a Skio-owned discount
 *   code into the Shopify session — see assets/skio-volume-discount.js
 *   and Skio's app embed, both of which call /discount/<SKIO_CODE>.
 *
 *   On this buy box we want one fixed promo code instead. Shopify holds
 *   only ONE session discount code at a time, so last write wins:
 *   calling /discount/<OUR_CODE> replaces whatever Skio set.
 *
 * WHAT IT DOES
 *   1. Sets window.skio_ran_tier_discount = true so Skio's tier script
 *      short-circuits and never pushes its own code from this page.
 *   2. Applies the code from [data-discount-code] on the buy box after
 *      every successful add to cart (the buy box fires theme:update:cart
 *      on success).
 *   3. Re-applies it when a checkout button is clicked, in case anything
 *      overwrote the session code in between. Capped by a short timeout so
 *      it can never stall checkout.
 *
 * SCOPE LIMIT
 *   This overrides *code-based* discounts only. A discount baked into the
 *   Skio selling plan itself is applied by Shopify at the line-item level
 *   and cannot be replaced by a code — it would stack with this one.
 */

(function () {
  'use strict';

  var STORAGE_KEY   = 'bundleDiscountOverride';
  var CHECKOUT_WAIT = 1200; // ms — hard cap on delaying a checkout click

  function init() {
    var buybox = document.querySelector('.bundle-subscription-buybox[data-discount-code]');
    if (!buybox) return;

    var code = (buybox.getAttribute('data-discount-code') || '').trim();
    if (!code) return;

    // Stop Skio's tier script from pushing its own code while on this page.
    window.skio_ran_tier_discount = true;

    /** Reads back which code we last pushed into the session (per tab). */
    function lastApplied() {
      try {
        return sessionStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return null;
      }
    }

    /**
     * Pushes the code into the Shopify session, replacing any code already
     * set (Skio's included). Redirecting to /cart.js keeps the response small.
     * @returns {Promise}
     */
    function applyCode() {
      return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Discount request failed (' + res.status + ')');
          try {
            sessionStorage.setItem(STORAGE_KEY, code);
          } catch (e) {
            /* private mode — the discount still applied, we just can't remember it */
          }
        })
        .catch(function (err) {
          console.error('[bundle-discount-override] Could not apply ' + code + ':', err);
        });
    }

    // ── Apply after every add to cart ─────────────────────────────
    // bundle-subscription.js dispatches theme:update:cart on a successful
    // /cart/add.js; the theme fires it for drawer quantity changes too.
    // Re-applying is idempotent, so both cases are safe.
    document.addEventListener('theme:update:cart', function () {
      applyCode();
    });

    // ── Re-apply on checkout ──────────────────────────────────────
    // Only engages once we have applied the code from this page, so it never
    // clobbers a code the customer entered elsewhere.
    document.addEventListener(
      'click',
      function (event) {
        var trigger = event.target && event.target.closest
          ? event.target.closest('[name="checkout"]')
          : null;
        if (!trigger) return;
        if (lastApplied() !== code) return;
        if (trigger.dataset.bundleDiscountReapplied === 'true') return;

        event.preventDefault();
        event.stopPropagation();
        trigger.dataset.bundleDiscountReapplied = 'true';

        Promise.race([
          applyCode(),
          new Promise(function (resolve) {
            setTimeout(resolve, CHECKOUT_WAIT);
          })
        ]).then(function () {
          trigger.click();
        });
      },
      true // capture — run before the theme's own checkout handlers
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
